import React, {
    useContext,
    useCallback,
    useEffect,
    useState,
    useRef
} from 'react';
import {
    get, filter, map, sortBy, uniqBy
} from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { AmfamOktaTokenContext } from 'e1p-capability-gateway-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'e1p-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import appConfig from 'app-config';
import { CoverageUtil } from 'e1p-portals-util-js';
import messages from '../QuotePage.messages';
import metadata from '../QuotePage.metadata.json5';

function structureClauseTableData(coverage) {
    // putting ID into an object as the Jutro table component expects an object
    return {
        publicID: coverage.publicID
    };
}

function generateClauseData(columnData, coveragePath) {
    return columnData.map(({ lob, code }) => {
        const completeCoveragePath = `coverages.${coveragePath}`;

        return {
            code,
            path: `${lob.path}.${completeCoveragePath}`,
            clauses: get(lob.data, completeCoveragePath.replace(/\.children/, ''))
        };
    });
}

const getCoveragesUniqueID = (submissionVM) => {
    const offerings = get(submissionVM, 'lobData.personalUmbrella_EU.offerings.value');
    const coverages = uniqBy(offerings.flatMap((offering) => (
        offering.coverages.coverages.map(structureClauseTableData)
    )), 'publicID');

    return {
        coverages
    };
};

const generateColumnData = (submissionVM) => {
    const lobOfferingPath = 'lobData.personalUmbrella_EU.offerings';
    const quoteOfferingPath = 'quoteData.offeredQuotes';

    const lobOfferings = get(submissionVM, `${lobOfferingPath}.value`);
    const quoteOfferings = get(submissionVM, `${quoteOfferingPath}.value`) || [];

    const columnData = lobOfferings.map((lobOffering, lobIndex) => {
        const quoteDataIndex = quoteOfferings.findIndex(
            (qdOffering) => qdOffering.branchCode === lobOffering.branchCode
        );
        const quoteData = quoteOfferings[quoteDataIndex];

        return {
            name: lobOffering.branchName,
            code: lobOffering.branchCode,
            quote: {
                path: `${quoteOfferingPath}.children[${quoteDataIndex}]`,
                data: quoteData
            },
            lob: {
                path: `${lobOfferingPath}.children[${lobIndex}]`,
                data: lobOffering
            }
        };
    });

    return sortBy(columnData, ['code']);
};

const generateTableData = (submissionVM, columnData, translator) => {
    const uniqueID = getCoveragesUniqueID(submissionVM);

    const coverages = {
        header: translator(messages.policyLevelCoverages),
        data: uniqueID.coverages,
        tableContent: generateClauseData(columnData, 'coverages')
    };

    return [coverages];
};

function ViewQuotePage(props) {
    const {
        wizardData: submissionVM
    } = props;

    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext);
    const { opCo } = useContext(AmfamOktaTokenContext);
    const [staleQuoteBranchCode, setStaleQuoteBranchCode] = useState(undefined);
    const { authHeader, authUserData } = useAuthentication();
    const showQuoteStartDate = appConfig.showQuoteStartDateInHeader;
    const isQuickQuote = useRef(
        get(submissionVM.value, 'quoteType') === 'Quick'
    ).current;


    const buyNow = useCallback(
        async () => submissionVM,
        [submissionVM]
    );

    const generateOverrides = useCallback(() => {
        const columnData = generateColumnData(submissionVM);
        const { quoteID } = submissionVM;
        const { underwritingIssues } = props;

        return {
            '@field': {
                // apply to all fields
                labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
                readOnly: true
            },
            quotePageLoadingIndicator: {
                loaded: true,
                text: translator(messages.creatingYourOfferingMessage)
            },
            quoteTable: {
                columnData,
                tableData: generateTableData(submissionVM, columnData, translator),
                underwritingIssues,
                quoteID: quoteID.value,
                submissionVM,
                updateWizardData: () => {},
                authHeader,
                viewOnlyMode: true,
                modifiers: get(submissionVM, 'lobData.personalUmbrella_EU.modifiers.value', []),
                authUserData,
                setFieldsChangedOnCoveragePage: () => { },
                opCo
            },
            startDate: {
                visible: showQuoteStartDate
            },
            print: {
                visible: !showQuoteStartDate
            },
            personalLiabilityLimitUwIssueInfoMessageDiv: {
                visible: false
            },
            coveragePrioirPolicyBoundMessageDiv: {
                visible: false
            },
            quoteProposalFailureErrorDiv: {
                visible: false
            },
            paperlessEmailChangedMessageDiv: {
                visible: false
            }
        };
        // prevent complaining props and showQuoteStartDate is missing in the dependencies list
        // watch props will cause refresh function ref multiple time,
        // showQuoteStartDate is generated from build time, so won't change in the runtime
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakpoint, submissionVM, translator, opCo]);

    useEffect(() => {
        const offerings = get(submissionVM, 'quoteData.offeredQuotes.value');
        const draftOffers = filter(offerings, ['status', 'Draft']);
        const draftBranchCodes = map(draftOffers, 'branchCode');

        setStaleQuoteBranchCode(draftBranchCodes);
        // Above logic only needs to run once when component is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // overriding coverage help text for given clause.
    useEffect(() => {
        get(submissionVM, 'value.lobData.personalUmbrella_EU.offerings[0].coverages.coverages').forEach((clause) => {
            const coveragesHelpText = CoverageUtil.getCoveragesHelpTextForEU(clause);

            if (coveragesHelpText) {
                clause.description = coveragesHelpText
            }
        })
    }, [submissionVM]);


    const onStaleQuoteBranchCode = useCallback(() => staleQuoteBranchCode, [staleQuoteBranchCode]);

    return (
        <WizardPage
            shouldLink
            showNext={get(submissionVM, 'value.quoteType') === 'Full'}
            finish={isQuickQuote}
        >
            {({ onNext }) => {
                const resolvers = {
                    resolveCallbackMap: {
                        onBuyNow: (lobPath, quotePath) => buyNow(lobPath, quotePath).then(onNext),
                        onStaleQuoteBranchCode,
                        onRecalculate: () => { },
                        onResetQuote: () => { },
                        onChangeSubmissionAndSync: () => { },
                        onChangeSubmission: () => { },
                        onSyncCoverages: () => { },
                        onPrint: () => { },
                    },
                };

                return (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={submissionVM}
                        overrideProps={generateOverrides()}
                        callbackMap={resolvers.resolveCallbackMap}
                    />
                );
            }}
        </WizardPage>
    );
}

ViewQuotePage.propTypes = wizardProps;
export default ViewQuotePage;
